import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseExpiry } from '@/lib/payments/paymentAccount.format';
import type { AddPaymentCardInput } from '@/lib/payments/paymentAccount.types';
import { requestCreateCardProfile, requestVerifyCard } from './paymentApi';
import { memberBillingProfileKey } from './useMemberBillingProfile';

interface UseAddPaymentCardOptions {
  onSuccess?: () => void;
}

/**
 * Submits a new credit card via the two-step gateway flow: verify (raw PAN/CVV → token) then
 * create the profile (token + last4 only). Raw PAN/CVV never reach the create step or telemetry.
 */
export function useAddPaymentCard({ onSuccess }: UseAddPaymentCardOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: AddPaymentCardInput) => {
      const pan = input.cardNumber.replace(/\D/g, '');
      // NOTE(Phase 9/BE): confirm whether verify/create expect a 2- or 4-digit ExpirationYear —
      // legacy sent 2-digit to verify but 4-digit to create. We send 4-digit; adjust on BE confirm.
      const { month: expirationMonth, year: expirationYear } = parseExpiry(input.expiry);

      const verified = await requestVerifyCard({ cardNumber: pan, cvv: input.cvv, expirationMonth, expirationYear });

      return requestCreateCardProfile({
        creditCardInfo: {
          last4: pan.slice(-4),
          nameOnCard: input.billing.name,
          hsa: input.isHsa,
          expirationMonth,
          expirationYear,
          paymentType: verified.paymentType,
        },
        gatewayTokenId: verified.gatewayTokenId,
        authTransactionId: verified.authTransactionId,
        isDefault: input.makeDefault,
        autoPay: input.autoPay,
        billing: input.billing,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberBillingProfileKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const code = extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'addPaymentCard', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return { submit: (input: AddPaymentCardInput) => mutation.mutate(input), isPending: mutation.isPending };
}
